// Phase 5: Advanced Analytics Dashboard
// Real-time performance analytics - KAS Server kompatibel

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import './AdvancedAnalytics.css';

const AdvancedAnalytics = () => {
  const { user, token } = useContext(AuthContext);
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('week');
  const [selectedMetric, setSelectedMetric] = useState('earnings');

  // Fetch analytics data
  const fetchAnalytics = async () => {
    if (!token || !user) return;

    setLoading(true);
    try {
      let endpoint = '';
      
      if (user.role === 'DRIVER') {
        endpoint = `/api/analytics/driver/${user.id}`;
      } else if (user.role === 'ADMIN' || user.role === 'SUPERADMIN') {
        endpoint = '/api/analytics/system';
      }

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch performance trends
  const fetchPerformance = async () => {
    if (!token || user?.role !== 'DRIVER') return;

    try {
      const response = await fetch('/api/analytics/performance', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPerformance(data.performance || []);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Performance-Daten:', error);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Calculate trend
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  // Get trend color
  const getTrendColor = (trend) => {
    if (trend > 0) return '#2ecc71';
    if (trend < 0) return '#e74c3c';
    return '#95a5a6';
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend > 5) return '📈';
    if (trend < -5) return '📉';
    return '➖';
  };

  // Render metric card
  const renderMetricCard = (title, value, trend, icon, color = '#3498db') => (
    <div className="metric-card" style={{ borderLeftColor: color }}>
      <div className="metric-header">
        <span className="metric-icon">{icon}</span>
        <span className="metric-title">{title}</span>
      </div>
      <div className="metric-value">{value}</div>
      {trend !== undefined && (
        <div className="metric-trend" style={{ color: getTrendColor(trend) }}>
          <span className="trend-icon">{getTrendIcon(trend)}</span>
          <span className="trend-value">{formatPercentage(Math.abs(trend))}</span>
          <span className="trend-period">vs. letzte Woche</span>
        </div>
      )}
    </div>
  );

  // Render performance chart (simplified version)
  const renderPerformanceChart = () => {
    if (!performance.length) return null;

    const maxValue = Math.max(...performance.map(p => p[selectedMetric] || 0));
    
    return (
      <div className="performance-chart">
        <div className="chart-header">
          <h3>Performance Trend (7 Tage)</h3>
          <div className="metric-selector">
            <button 
              className={selectedMetric === 'earnings' ? 'active' : ''}
              onClick={() => setSelectedMetric('earnings')}
            >
              Einnahmen
            </button>
            <button 
              className={selectedMetric === 'trips' ? 'active' : ''}
              onClick={() => setSelectedMetric('trips')}
            >
              Fahrten
            </button>
            <button 
              className={selectedMetric === 'avg_duration' ? 'active' : ''}
              onClick={() => setSelectedMetric('avg_duration')}
            >
              Ø Dauer
            </button>
          </div>
        </div>
        
        <div className="chart-container">
          {performance.map((day, index) => {
            const value = day[selectedMetric] || 0;
            const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const date = new Date(day.date);
            
            return (
              <div key={index} className="chart-bar-container">
                <div 
                  className="chart-bar"
                  style={{ height: `${height}%` }}
                  title={`${date.toLocaleDateString('de-DE')}: ${
                    selectedMetric === 'earnings' ? formatCurrency(value) :
                    selectedMetric === 'trips' ? `${value} Fahrten` :
                    `${Math.round(value)} Min`
                  }`}
                >
                  <div className="bar-value">
                    {selectedMetric === 'earnings' ? formatCurrency(value) : Math.round(value)}
                  </div>
                </div>
                <div className="chart-label">
                  {date.toLocaleDateString('de-DE', { weekday: 'short' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Load data on component mount
  useEffect(() => {
    if (user && token) {
      fetchAnalytics();
      if (user.role === 'DRIVER') {
        fetchPerformance();
      }
    }
  }, [user, token]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!user || !token) return;

    const interval = setInterval(() => {
      fetchAnalytics();
      if (user.role === 'DRIVER') {
        fetchPerformance();
      }
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [user, token]);

  if (!user || loading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner"></div>
        <span>Analytics werden geladen...</span>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="analytics-error">
        <span>📊</span>
        <p>Analytics-Daten konnten nicht geladen werden</p>
        <button onClick={fetchAnalytics}>Erneut versuchen</button>
      </div>
    );
  }

  return (
    <div className="advanced-analytics">
      <div className="analytics-header">
        <h2>📊 Analytics Dashboard</h2>
        <div className="refresh-controls">
          <button onClick={fetchAnalytics} className="refresh-btn">
            🔄 Aktualisieren
          </button>
        </div>
      </div>

      {/* Driver Analytics */}
      {user.role === 'DRIVER' && analytics.statistics && (
        <>
          <div className="metrics-grid">
            {renderMetricCard(
              'Gesamteinnahmen', 
              formatCurrency(analytics.statistics.all_time.total_earnings),
              undefined,
              '💰',
              '#2ecc71'
            )}
            {renderMetricCard(
              'Heute',
              formatCurrency(analytics.statistics.today.today_earnings),
              undefined,
              '📅',
              '#3498db'
            )}
            {renderMetricCard(
              'Diese Woche',
              formatCurrency(analytics.statistics.this_week.week_earnings),
              undefined,
              '📈',
              '#9b59b6'
            )}
            {renderMetricCard(
              'Abgeschlossene Fahrten',
              analytics.statistics.all_time.completed_trips,
              undefined,
              '🚖',
              '#e67e22'
            )}
          </div>

          <div className="secondary-metrics">
            {renderMetricCard(
              'Ø Fahrtpreis',
              formatCurrency(analytics.statistics.all_time.avg_fare),
              undefined,
              '💵'
            )}
            {renderMetricCard(
              'Ø Fahrtdauer',
              `${Math.round(analytics.statistics.all_time.avg_duration || 0)} Min`,
              undefined,
              '⏱️'
            )}
            {renderMetricCard(
              'Erfolgsrate',
              formatPercentage(
                (analytics.statistics.all_time.completed_trips / 
                 analytics.statistics.all_time.total_trips) * 100
              ),
              undefined,
              '✅'
            )}
          </div>

          {/* Performance Chart */}
          {renderPerformanceChart()}

          {/* Company Information */}
          {analytics.companies && analytics.companies.length > 0 && (
            <div className="company-info">
              <h3>🏢 Verknüpfte Unternehmen</h3>
              <div className="company-list">
                {analytics.companies.map(company => (
                  <div key={company.id} className="company-item">
                    <div className="company-name">{company.name}</div>
                    <div className="company-status">
                      <span className={`status-badge ${company.status}`}>
                        {company.status === 'active' ? '✅ Aktiv' : '⏸️ Inaktiv'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Admin Analytics */}
      {(user.role === 'ADMIN' || user.role === 'SUPERADMIN') && analytics.statistics && (
        <>
          <div className="metrics-grid">
            {renderMetricCard(
              'Gesamtumsatz',
              formatCurrency(analytics.statistics.total_revenue),
              undefined,
              '💰',
              '#2ecc71'
            )}
            {renderMetricCard(
              'Aktive Fahrer',
              analytics.active_drivers || 0,
              undefined,
              '👥',
              '#3498db'
            )}
            {renderMetricCard(
              'Heute Fahrten',
              analytics.statistics.today_trips || 0,
              undefined,
              '🚖',
              '#e67e22'
            )}
            {renderMetricCard(
              'Unternehmen',
              analytics.statistics.total_companies || 0,
              undefined,
              '🏢',
              '#9b59b6'
            )}
          </div>

          {/* Recent Activity */}
          {analytics.recent_activity && (
            <div className="recent-activity">
              <h3>📊 Letzte Aktivitäten</h3>
              <div className="activity-list">
                {analytics.recent_activity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="activity-item">
                    <span className="activity-icon">🚖</span>
                    <span className="activity-description">{activity.description}</span>
                    <span className="activity-time">
                      {new Date(activity.timestamp).toLocaleTimeString('de-DE')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdvancedAnalytics;
